/**
 * Offline VIN-Validierung (kein API-Call nötig).
 *
 * Liefert für eine 17-stellige Fahrgestellnummer (VIN) sofort:
 *  • Prüfziffer-Validität (Mod-11-Checksumme — global gültig für viele Hersteller)
 *  • Hersteller anhand WMI (World Manufacturer Identifier, erste 3 Zeichen)
 *  • Modelljahr anhand des 10. Zeichens
 *  • Produktionswerk anhand des 11. Zeichens (bei einigen Herstellern)
 *
 * Damit sieht der User sofort, ob die Eingabe plausibel ist — auch wenn die
 * DAT-Lupe (noch) nicht freigeschaltet ist. Komplett offline, kostenlos,
 * funktioniert für jede VIN weltweit.
 */

// World Manufacturer Identifier — wichtigste deutsche & internationale Hersteller
const WMI_MAP = {
  // Deutsche Hersteller
  WBA: { make: "BMW", note: "BMW AG, München" },
  WBS: { make: "BMW M", note: "BMW Motorsport / ///M" },
  WBX: { make: "BMW", note: "BMW (SUV-Reihe)" },
  WBY: { make: "BMW i", note: "BMW i-Reihe (Elektro)" },
  "4US": { make: "BMW", note: "BMW USA (Spartanburg)" },
  WDB: { make: "Mercedes-Benz", note: "Mercedes-Benz PKW" },
  WDC: { make: "Mercedes-Benz", note: "Mercedes-Benz SUV (USA)" },
  WDD: { make: "Mercedes-Benz", note: "Mercedes-Benz (neuere PKW)" },
  WDF: { make: "Mercedes-Benz", note: "Mercedes-Benz Transporter" },
  W1K: { make: "Mercedes-Benz", note: "Mercedes-Benz (ab 2020)" },
  W1N: { make: "Mercedes-Benz", note: "Mercedes-Benz SUV (ab 2020)" },
  WDR: { make: "Mercedes-AMG", note: "Mercedes-AMG GmbH" },
  WMX: { make: "Mercedes-AMG", note: "Mercedes-AMG" },
  WVW: { make: "Volkswagen", note: "Volkswagen PKW" },
  WV1: { make: "Volkswagen", note: "VW Nutzfahrzeuge" },
  WV2: { make: "Volkswagen", note: "VW Bus/Transporter" },
  WV3: { make: "Volkswagen", note: "VW Bus/Transporter" },
  WVG: { make: "Volkswagen", note: "VW SUV (Tiguan, Touareg)" },
  WAU: { make: "Audi", note: "Audi AG, Ingolstadt" },
  WA1: { make: "Audi", note: "Audi SUV (Q-Reihe)" },
  WUA: { make: "Audi Sport", note: "Audi Sport (RS-Modelle)" },
  WP0: { make: "Porsche", note: "Porsche AG, Stuttgart" },
  WP1: { make: "Porsche", note: "Porsche SUV (Cayenne, Macan)" },
  WMW: { make: "MINI", note: "MINI (BMW Gruppe)" },
  WF0: { make: "Ford", note: "Ford-Werke, Köln" },
  WF1: { make: "Ford", note: "Ford-Werke (Nutzfahrzeuge)" },
  // Weitere EU-Hersteller
  VF1: { make: "Renault", note: "Renault SA, Frankreich" },
  VF3: { make: "Peugeot", note: "Peugeot SA, Frankreich" },
  VF7: { make: "Citroën", note: "Citroën, Frankreich" },
  VF8: { make: "Matra/Talbot", note: "Matra, Frankreich" },
  ZFA: { make: "Fiat", note: "Fiat SpA, Italien" },
  ZFF: { make: "Ferrari", note: "Ferrari SpA, Maranello" },
  ZAR: { make: "Alfa Romeo", note: "Alfa Romeo, Italien" },
  ZAM: { make: "Maserati", note: "Maserati SpA, Italien" },
  ZHW: { make: "Lamborghini", note: "Lamborghini, Italien" },
  KMH: { make: "Hyundai", note: "Hyundai Motor Co., Südkorea" },
  KNA: { make: "Kia", note: "Kia Motors, Südkorea" },
  KND: { make: "Kia", note: "Kia (SUV)" },
  // Japanische
  JT2: { make: "Toyota", note: "Toyota Motor Corp., Japan" },
  JTD: { make: "Toyota", note: "Toyota (PKW)" },
  JTH: { make: "Lexus", note: "Lexus (Toyota-Premium)" },
  JN1: { make: "Nissan", note: "Nissan Motor Co., Japan" },
  JHM: { make: "Honda", note: "Honda Motor Co., Japan" },
  JF1: { make: "Subaru", note: "Subaru, Japan" },
  JMB: { make: "Mitsubishi", note: "Mitsubishi Motors, Japan" },
  JMZ: { make: "Mazda", note: "Mazda Motor Corp., Japan" },
  // Sonstige
  YV1: { make: "Volvo", note: "Volvo Cars, Schweden" },
  YS3: { make: "Saab", note: "Saab Automobile, Schweden" },
  TMB: { make: "Škoda", note: "Škoda Auto, Tschechien" },
  TMP: { make: "Škoda", note: "Škoda (Yeti, Karoq)" },
  VSS: { make: "SEAT", note: "SEAT SA, Spanien" },
  VSE: { make: "SEAT", note: "SEAT" },
  SJN: { make: "Nissan", note: "Nissan UK (Sunderland)" },
  SAL: { make: "Land Rover", note: "Land Rover / Range Rover, UK" },
  SAJ: { make: "Jaguar", note: "Jaguar Cars Ltd., UK" },
  // US
  "1G1": { make: "Chevrolet", note: "Chevrolet, USA" },
  "1G6": { make: "Cadillac", note: "Cadillac, USA" },
  "1FA": { make: "Ford", note: "Ford Motor Co., USA" },
  "1FT": { make: "Ford", note: "Ford Trucks, USA" },
  "1HG": { make: "Honda", note: "Honda USA" },
  "5YJ": { make: "Tesla", note: "Tesla Inc., USA" },
};

// 10. Zeichen → Modelljahr
const YEAR_CODE_MAP = {
  A: [1980, 2010],
  B: [1981, 2011],
  C: [1982, 2012],
  D: [1983, 2013],
  E: [1984, 2014],
  F: [1985, 2015],
  G: [1986, 2016],
  H: [1987, 2017],
  J: [1988, 2018],
  K: [1989, 2019],
  L: [1990, 2020],
  M: [1991, 2021],
  N: [1992, 2022],
  P: [1993, 2023],
  R: [1994, 2024],
  S: [1995, 2025],
  T: [1996, 2026],
  V: [1997, 2027],
  W: [1998, 2028],
  X: [1999, 2029],
  Y: [2000, 2030],
  "1": [2001, 2031],
  "2": [2002, 2032],
  "3": [2003, 2033],
  "4": [2004, 2034],
  "5": [2005, 2035],
  "6": [2006, 2036],
  "7": [2007, 2037],
  "8": [2008, 2038],
  "9": [2009, 2039],
};

// Mod-11-Checksumme (US/Asien-Standard; in EU optional, daher nicht hart erzwingen)
const TRANSLITERATION = {
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
  J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9,
  S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
};

const WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

function computeCheckDigit(vin) {
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const ch = vin[i];
    const val = /\d/.test(ch) ? Number(ch) : TRANSLITERATION[ch];
    if (val === undefined) return null;
    sum += val * WEIGHTS[i];
  }
  const remainder = sum % 11;
  return remainder === 10 ? "X" : String(remainder);
}

/**
 * Hauptfunktion: validiert eine VIN und liefert ein Info-Objekt.
 *
 * @param {string} raw - Roh-Eingabe (wird automatisch getrimmt und in Großbuchstaben gewandelt)
 * @returns {{
 *   valid: boolean,
 *   normalized: string,
 *   length: number,
 *   make: string | null,
 *   makeNote: string | null,
 *   year: number | null,
 *   yearRange: string | null,
 *   checkDigit: string | null,
 *   checksumValid: boolean | null,
 *   errors: string[]
 * }}
 */
export function validateVIN(raw) {
  const vin = (raw || "").toUpperCase().replace(/\s+/g, "");
  const errors = [];

  if (!vin) {
    return {
      valid: false, normalized: "", length: 0, make: null, makeNote: null,
      year: null, yearRange: null, checkDigit: null, checksumValid: null, errors: [],
    };
  }

  if (vin.length !== 17) {
    errors.push(`VIN muss exakt 17 Zeichen lang sein (aktuell: ${vin.length})`);
  }

  // I, O, Q dürfen nicht vorkommen
  if (/[IOQ]/.test(vin)) {
    errors.push("VIN darf die Buchstaben I, O, Q nicht enthalten");
  }

  // Nur erlaubte Zeichen
  if (!/^[A-HJ-NPR-Z0-9]+$/.test(vin)) {
    errors.push("VIN enthält ungültige Zeichen");
  }

  // WMI auflösen
  const wmi3 = vin.slice(0, 3);
  const wmi2 = vin.slice(0, 2);
  const wmiInfo = WMI_MAP[wmi3] || null;

  // Modelljahr
  let year = null;
  let yearRange = null;
  if (vin.length >= 10) {
    const code = vin[9];
    const years = YEAR_CODE_MAP[code];
    if (years) {
      yearRange = `${years[0]} oder ${years[1]}`;
      // bei VINs ab 2010 ist Position 7 ein Buchstabe, davor eine Ziffer
      const pos7IsLetter = /[A-Z]/.test(vin[6] || "");
      year = pos7IsLetter ? years[1] : years[0];
    }
  }

  // Checksumme (Mod-11 — bei EU-Herstellern oft nicht eingehalten)
  let checkDigit = null;
  let checksumValid = null;
  if (vin.length === 17 && /^[A-HJ-NPR-Z0-9]+$/.test(vin)) {
    checkDigit = computeCheckDigit(vin);
    if (checkDigit !== null) {
      checksumValid = vin[8] === checkDigit;
    }
  }

  return {
    valid: errors.length === 0,
    normalized: vin,
    length: vin.length,
    wmi: wmi3,
    make: wmiInfo?.make || null,
    makeNote: wmiInfo?.note || null,
    year,
    yearRange,
    checkDigit,
    checksumValid,
    errors,
  };
}
